import { useContext, useEffect, useState } from 'react';
import SmartTable from '../../uiControls/SmartTable';
import { ApplicableTableSettings } from '../../tableSettings';
import useTableStorage from '../../hooks/useTableStorage';
import ApplicationContext from '../../ApplicationContext';
import useSystemSettings from '../../hooks/useSystemSettings';
import useGlobalOkDialog from '../../hooks/useGlobalOkDialog';
import { AccountBalanceModel, IAccountBalanceDetail, IAccountBalanceHeader } from '../../fms.core/AccountBalanceModel';
import { accountBalanceTable } from './tableSettings';
import { JobNames } from '../../framework.core/JobNames';
import SecondaryButton from '../../ui/SecondaryButton';
import Label from '../../ui/Label';
import moment from 'moment';
import { AccountBalanceByEntityArgs, NewAgingCommonArgs } from '../../fms.report/args';
import { DocumentType } from "../../framework.core/DocumentType";
import useReportViewer from '../../hooks/useReportViewer';
import { getDecimal } from '../../util';
import numeral from 'numeral';
import { now } from '../../dateUtils';

function getType(glDesc: string) {
  //iFMS3.3 AccountBalancebyEntityReport > GetInitData
  switch (glDesc) {
    case 'AR AGENT':
      return 'AgentAR';
    case 'AP AGENT':
      return 'AgentAP';
    case 'ACCOUNT RECEIVABLE':                //111400
      return 'AR';
    case 'ACCOUNT PAYABLE':                   //211400
      return 'AP';
    case 'A/R AGENT':                         //111410
      return 'AAR';
    case 'A/P AGENT':                         //211410
      return 'AAP';
    case 'RECEIVABLES':                       //111600
      return 'REC';
    case 'PAYABLE':                           //211600
      return 'PAY';
    case 'CHECK RECEIVABLE':                  //111500
      return 'CREC';
    case 'CHECK PAYABLE':                     //211500
      return 'CPAY';
    case 'ADVANCE PAYMENT':                   //112100
      return 'ADVR';
    case 'ADVANCE RECEIVED':                  //212100
      return 'ADVP';
    case 'DEFERRED SALES':                    //211700
      return 'DEFS';
    case 'DEFERRED COST':                     //111700
      return 'DEFC';
    case 'DUTY AR':                           //112600
      return 'DTAR';
    case 'DUTY AP':                           //212600
      return 'DTAP';
    case 'DUTY AR AGENT':                     //112620
      return 'DARA';
    case 'DUTY AP AGENT':                     //212620
      return 'DAPA';      
    case 'LOAN TO AGENT':                     //112500
      return 'LTAG';
    case 'COLLECT FOR AGENT':                 //212500
      return 'CFAG';
    case 'ADVANCE DUTY RECEIVED (CUSTOMER)':  //212630
      return 'DTCU'; 
    case 'ADVANCE DUTY PAYMENT (AGENT)':      //112630
      return 'DTAG';
    case 'SECURITY DEPOSIT':                  //121100
      return 'DS';
    case 'TAX RECEIVABLES':                    //111610
      return 'TAXREC';
    case 'TAX PAYABLE':                       //212410
      return 'TAXPAY';
    default:
      return glDesc;
  }
}

function GlDescRenderer({ cell, data, row }: any) {
  const { viewReport } = useReportViewer();

  const l = data[row.index];

  const color: any = data[row.index].sectionStart ? 'blue' : null;
  const fontWeight: any = data[row.index].sectionStart ? 'bold' : null;

  if (l.isTotalLine)
    return (
      <div style={{ paddingLeft: '45px', height: '100%', width: '100%' }}>
        <div style={{ borderTop: '1px solid #444444' }}>
          {cell.value}
        </div>
      </div>)
  else if (l.sectionStart)
    return <div style={{ color, fontWeight, paddingLeft: '5px', textAlign: 'left', height: '100%', cursor:'pointer' }}>
      <span
        onClick={() => {
          let title = '';
          let args = null;
          let type = getType(l.glDesc);

          if (type === 'TAXPAY' || type === 'TAXREC') {
            title = `Account Balance : ${cell.value}`;
            let glNo = type === 'TAXPAY' ? '212410' : '111610';

            args = new AccountBalanceByEntityArgs({
              docType: DocumentType.AcctBalanceByEntity2,
              formCaption: title,
              subject: title,

              toDate: now(),
              comType: l.comType,
              comId: l.comId,
              comName: l.customerName,
              fromGLNo: glNo,
              toGLNo: glNo,
              branch: l.branch,
              subCode: l.subCode,
              salesCostOnly: '',
            })
          } else {
              title =  `${cell.value} Balance Detail`;

              args = new NewAgingCommonArgs({
              docType: DocumentType.NewGLBalanceDetail,
              formCaption: title,
              subject: title,
              comId: l.comId,
              comType: l.comType,
              endingDate: l.toDate,
              type,
              branch: l.branch,
              subCode: l.subCode,
            })
          }
          viewReport(args, "", true);
        }}>
        {cell.value}
      </span>
    </div>;
  else
    return null;
}


function AmountRenderer(label: string) {
  return ({ data, row }: any) => {
    const l = data[row.index];

    let amount = '';
    const cellValue = l[label];

    const decimals = getDecimal(l.currency);
    if (cellValue !== null && cellValue !== undefined && cellValue !== '') {
      const format = `0,0.${'0'.repeat(decimals)}`;
      amount = numeral(cellValue).format(format);
    }
    return (
      <div className="ifms-table-align-right">{parseFloat(amount) === 0 ? null : amount}</div>
    );
    
  };
}

function CurrencyRenderer({ data, row }: any) {
  const l = data[row.index];

  return (
    <div className="ifms-table-default">
      {l.currency === l.primaryCurrency && l.isSameGroup ? `>>${l.currency}` : l.currency}
    </div>
  );
}

function AccountBalanceImpl({
  jobName,
  jobId,
  toDate,
  comId,
  comType,
  branch,
  subCode,
  customerName,
  onPopupClose
}: {
  jobName: JobNames,
  jobId: number,
  toDate: string,
  comId: number,
  comType: string,
  branch: string,
  subCode: string,
  customerName: string,
  onPopupClose: () => void,
}) {
  const systemSettings = useSystemSettings();

  const primaryCurrency = systemSettings.primaryCurrency ?? '';

  const appContext = useContext(ApplicationContext);
  const tableStorage = useTableStorage();
  const tableId = `${jobName}-AccountBalance-${jobId}`;

  const [showGlobalOkDialog] = useGlobalOkDialog();

  const [accountBalanceData] = useState<IAccountBalanceHeader>({ glDesc: '', groups: [], lineItems: [] });
  const [initialized, setInitialized] = useState<boolean>(false);

  async function loadData() {
    const apiResult = await appContext.api.getAccountBalance(toDate, comId, comType, branch, subCode);
    //if ( !apiResult || !apiResult.success) {
    if (!apiResult || !apiResult.success) {
      await showGlobalOkDialog(<p>Fail load data.</p>, false);
    } else {
      (apiResult?.result as AccountBalanceModel[]).forEach(x => {
        createHeader(accountBalanceData, x);
      })
      setInitialized(true);
    }
  }

  function createHeader(mg: IAccountBalanceHeader, prs: AccountBalanceModel) {
    const mgi: IAccountBalanceHeader = { glDesc: prs.GLDesc, groups: [], lineItems: [], };
    if (mg.groups.some(x => x.glDesc === prs.GLDesc)) {
      mg.groups.find(x => x.glDesc === prs.GLDesc)?.lineItems.push(createDetail(prs));
    }
    else {
      mg.groups.push(mgi);
      mgi.lineItems.push(createDetail(prs));
    }
  }

  function createDetail(pr: AccountBalanceModel): IAccountBalanceDetail {
    return {
      glNo: pr.GLNo,
      glDesc: pr.GLDesc,
      currency: pr.Currency,
      amount1: pr.Amount1,
      amount2: pr.Amount2,
      amount3: pr.Amount3,
      amountOld: pr.AmountOld,
      amountTot: pr.AmountTot,
      amountF1: pr.AmountF1,
      amountF2: pr.AmountF2,
      amountF3: pr.AmountF3,
      amountFOld: pr.AmountFOld,
      amountFTot: pr.AmountFTot,
    }
  }

  useEffect(() => {
    if (!initialized) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!initialized) {
    return (
      <div style={{ minHeight: '400px' }}></div>
    );
  }

  function accountBalanceToTable(p: any) {
    const lineItems: Array<any> = [];

    p.groups.forEach((g: any) => {
      g.lineItems.forEach((l: any, i: number) => {
        lineItems.push({
          sectionStart: i === 0,
          sectionEnd: false,
          groupStart: true,
          groupEnd: l.currency === primaryCurrency,
          isSameGroup: l.currency !== primaryCurrency,
          groupNum: i,
          glDesc: l.glDesc,
          currency: l.currency,
          amount1: l.amountF1,
          amount2: l.amountF2,
          amount3: l.amountF3,
          amountOld: l.amountFOld,
          amountTot: l.amountFTot,
        });

        if (l.currency !== primaryCurrency) {
          lineItems.push({
            sectionStart: false,
            sectionEnd: false,
            groupStart: false,
            groupEnd: true,
            isSameGroup: true,
            groupNum: i,
            glDesc: l.glDesc,
            currency: `${primaryCurrency}`,
            amount1: l.amount1,
            amount2: l.amount2,
            amount3: l.amount3,
            amountOld: l.amountOld,
            amountTot: l.amountTot,
          });
        }
        //Total line
        if (i === g.lineItems.length - 1) {
          lineItems.push({
            sectionStart: false,
            sectionEnd: false,
            groupStart: false,
            groupEnd: false,
            isTotalLine: true,
            glDesc: 'Total',
            currency: primaryCurrency,
            amount1: lineItems.filter(x => x.currency === primaryCurrency && x.glDesc === l.glDesc).reduce((acc, item) => acc + item.amount1, 0),
            amount2: lineItems.filter(x => x.currency === primaryCurrency && x.glDesc === l.glDesc).reduce((acc, item) => acc + item.amount2, 0),
            amount3: lineItems.filter(x => x.currency === primaryCurrency && x.glDesc === l.glDesc).reduce((acc, item) => acc + item.amount3, 0),
            amountOld: lineItems.filter(x => x.currency === primaryCurrency && x.glDesc === l.glDesc).reduce((acc, item) => acc + item.amountOld, 0),
            amountTot: lineItems.filter(x => x.currency === primaryCurrency && x.glDesc === l.glDesc).reduce((acc, item) => acc + item.amountTot, 0),
          });
        }
      });
      //Empty row
      lineItems.push({
        sectionStart: false,
        sectionEnd: true,
        groupStart: false,
        groupEnd: false,
        glDesc: '',
        currency: '',
        amount1: 0,
        amount2: 0,
        amount3: 0,
        amountOld: 0,
        amountTot: 0,
      });
    });
    return lineItems;
  }

  function getAmountHeader(num: number) {
    return moment().subtract(num, 'months').format(('MMM YYYY'));
  }

  // function getPopupLabel(jobName: JobNames) {
  //   let returnData = ''
  //   switch (jobName) {
  //     case JobNames.Invoice: case JobNames.WHInvoice: case JobNames.WHStorageInv:case JobNames.Company:
  //       returnData = 'Customer Balance';
  //       break;
  //     case JobNames.GInvo:
  //       returnData = 'Invoice Balance';
  //       break;
  //     case JobNames.Bill: case JobNames.GBill:
  //       returnData = 'Vendor Balance';
  //       break;
  //     case JobNames.CrDb: case JobNames.WHCrDb:
  //       returnData = 'Agent Balance';
  //       break;
  //   }
  //   return returnData;
  // }

  const tableOptions: ApplicableTableSettings = {
    tableName: tableId,
    ...accountBalanceTable,
    height: '450px',
    data: accountBalanceToTable(accountBalanceData).map((x, i) => ({
      ...x,
      i,
      primaryCurrency,
      comId,
      comType,
      customerName,
      toDate,
      branch,
      subCode,
    })),
    rowRenderers: [
      { accessor: 'glDesc', Cell: GlDescRenderer },
      { accessor: 'currency', Cell: CurrencyRenderer },
      { accessor: 'amount1', Cell: AmountRenderer('amount1'), Header: () => <span>{getAmountHeader(0)}</span> },
      { accessor: 'amount2', Cell: AmountRenderer('amount2'), Header: () => <span>{getAmountHeader(1)}</span> },
      { accessor: 'amount3', Cell: AmountRenderer('amount3'), Header: () => <span>{getAmountHeader(2)}</span> },
      { accessor: 'amountOld', Cell: AmountRenderer('amountOld') },
      { accessor: 'amountTot', Cell: AmountRenderer('amountTot') },
    ],
    customStorage: tableStorage,
    getCustomCellProps: (currentProps: any, cell: any, rowIndex: any) => {
      if (cell.row.original.sectionStart) {
        currentProps.style.borderTop = '1px solid #444444';
      }
      else {
        if (
          cell.column.id === 'currency' ||
          cell.column.id === 'amount1' ||
          cell.column.id === 'amount2' ||
          cell.column.id === 'amount3' ||
          cell.column.id === 'amountOld' ||
          cell.column.id === 'amountTot') {
          if (cell.row.original.isTotalLine || cell.row.original.groupStart) {
            currentProps.style.borderTop = '1px solid #444444';
          }
          else if (cell.row.original.groupNum % 2 === 1 && cell.row.original.isSameGroup)
            currentProps.style.backgroundColor = '#F5F5F5';
        }
      }
      return currentProps;
    },
  };

  return (
    <div className='report-popup'>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', fontWeight: 'bold' }}>
        <div style={{ textAlign: 'center', paddingBottom: '10px' }}>
          <Label label='Balance' style={{ fontSize: '20px' }} />
        </div>
        <div style={{ textAlign: 'center', paddingBottom: '10px' }}>
          <Label label={`(${customerName})`} style={{ fontSize: '17px' }} />
        </div>
        <div style={{ textAlign: 'center' }}>
          <Label label={`As of ${moment().format(('MMMM DD, YYYY'))}`} style={{ fontSize: '14px' }} />
        </div>
        <div style={{ marginLeft: 'auto', marginRight: '70px', textAlign: 'right', paddingBottom: '10px' }}>
          <Label label={`Primary CUR: ${primaryCurrency}`} style={{ fontSize: '14px' }} />
        </div>
      </div>

      <div style={{ width: '700px' }}>
        <SmartTable {...tableOptions} />
      </div>

      <div className='report-footer'>
        <SecondaryButton
          size="regular"
          style={{ width: '90px' }}
          onClick={onPopupClose}>
          CLOSE
        </SecondaryButton>
      </div>
    </div>
  );
}

const AccountBalance = ({
  onPopupClose,
  jobId,
  jobName,
  toDate,
  comId,
  comType,
  branch,
  subCode,
  customerName,
}: {
  onPopupClose: () => void,
  jobId: number,
  jobName: JobNames,
  toDate: string,
  comId: number,
  comType: string,
  branch: string,
  subCode: string,
  customerName: string,
}) => {
  return (
    <AccountBalanceImpl
      jobName={jobName}
      jobId={jobId}
      onPopupClose={onPopupClose}
      key={`customer-balance-details`}
      toDate={toDate}
      comId={comId}
      comType={comType}
      branch={branch}
      subCode={subCode}
      customerName={customerName}
    />
  );
};

export default AccountBalance;
